import { DateTime } from 'luxon';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CsCallablePage } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { sleep } from '@cf-shared/shared-util';
import { InputCalendarPageParams, InputCalendarPageResult } from './input-calendar-interface';

import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatMonthYearLong, formatWeekdayDayMonthShort } from '@cf-shared/date-format';
import { TranslatedString, untranslatedString } from 'models/translated-string';
interface Month {
  index: number;
  name: string;
}

enum ViewMode {
  Calendar = 'calendar',
  YearPicker = 'year-picker',
  MonthPicker = 'month-picker'
}

@Component({
  selector: 'page-input-calendar',
  templateUrl: 'input-calendar-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 328, height: 'auto', fullScreenOnMobile: false }) }]
})
export class InputCalendarPage extends CsCallablePage<InputCalendarPageParams, InputCalendarPageResult> {
  protected LL = LL;
  @ViewChild('yearGrid') yearGridRef: ElementRef<HTMLElement>;

  title: string;
  date: DateTime;
  minDate: DateTime;
  maxDate: DateTime;
  selectedId: string;
  todayId: string;
  // formatted by the Luxon-based date-format helpers (already locale-aware), not catalog text
  monthYearHeader: TranslatedString;
  shortDateHeader: string;
  weekdays: Array<{ name: string }> = [];
  weeks: Array<{ days: Array<{ day: number; id: string; outOfMonth: boolean }> }> = [];
  years: number[] = [];
  viewMode: ViewMode = ViewMode.Calendar;
  ViewMode = ViewMode;

  viewDate: DateTime;
  months: Month[] = [
    { index: 1, name: 'Jan' },
    { index: 2, name: 'Feb' },
    { index: 3, name: 'Mar' },
    { index: 4, name: 'Apr' },
    { index: 5, name: 'May' },
    { index: 6, name: 'Jun' },
    { index: 7, name: 'Jul' },
    { index: 8, name: 'Aug' },
    { index: 9, name: 'Sep' },
    { index: 10, name: 'Oct' },
    { index: 11, name: 'Nov' },
    { index: 12, name: 'Dec' },
  ];

  constructor(
    private debug: DebugService,
    private settings: SettingsService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  // gets the first day of the display week (1=Mon, 6=Sat, 7=Sun in Luxon format)
  private getFirstDayOfWeek(): number {
    const displayOrder = this.settings.getWeekDayDisplayOrder();
    // displayOrder[0] is the ISO index (0-6) of the first display day
    // convert to Luxon weekday (1-7): ISO 0-5 → Luxon 1-6, ISO 6 → Luxon 7
    const isoFirst = displayOrder[0];
    return isoFirst === 6 ? 7 : isoFirst + 1;
  }

  // calculates the start of the display week for a given date
  private getDisplayWeekStart(date: DateTime): DateTime {
    const targetFirstDay = this.getFirstDayOfWeek();
    const currentWeekday = date.weekday; // 1=Mon, 7=Sun
    // calculate days to go back to reach the first day of the display week
    const daysBack = (currentWeekday - targetFirstDay + 7) % 7;
    return date.minus({ days: daysBack });
  }

  ngOnInit() {
    // use navParams.get() for callable pages (params stored in memory, not URL)
    const title = this.navParams.get('title') as string | undefined;
    const dateISO = this.navParams.get('dateISO') as string | undefined;
    const minDateISO = this.navParams.get('minDateISO') as string | undefined;
    const maxDateISO = this.navParams.get('maxDateISO') as string | undefined;

    this.title = title || 'Calendar';

    const thisDate = this.debug.getNowLocal();
    this.todayId = thisDate.toISODate();
    this.minDate = minDateISO
      ? DateTime.fromISO(minDateISO)
      : DateTime.fromObject({ month: thisDate.month, day: thisDate.day, year: thisDate.year - 100 });
    this.maxDate = maxDateISO
      ? DateTime.fromISO(maxDateISO)
      : DateTime.fromObject({ month: thisDate.month, day: thisDate.day, year: thisDate.year + 101 });

    for (let i = this.minDate.year; i < this.maxDate.year + 1; i++) {
      this.years.push(i);
    }

    const date = dateISO ? DateTime.fromISO(dateISO) : thisDate;
    this.selectDate(date);
    this.viewDate = date;
    this.updateUI();
  }

  selectDate(date: DateTime) {
    if (!date || !date.isValid) {
      date = DateTime.local();
    }

    if (date.toMillis() > this.maxDate.toMillis()) {
      date = this.maxDate;
    }

    if (date.toMillis() < this.minDate.toMillis()) {
      date = this.minDate;
    }
    this.date = date;
    this.selectedId = date.toISODate();
  }

  private generateWeeksForMonth(monthDate: DateTime): Array<{ days: Array<{ day: number; id: string; outOfMonth: boolean }> }> {
    const weeks = [];
    const firstDayOfMonth = monthDate.startOf('month');
    // adjust to the start of the display week containing the first day of the month
    const weekStart = this.getDisplayWeekStart(firstDayOfMonth);

    for (let i = 0, week = weekStart; i < 6; i++, week = week.plus({ weeks: 1 })) {
      const weekObject = { days: [] };
      weeks.push(weekObject);

      for (let j = 0, day = week; j < 7; j++, day = day.plus({ days: 1 })) {
        const dayObject = {
          day: day.day,
          id: day.toISODate(),
          outOfMonth: !day.hasSame(monthDate, 'month'),
        };
        weekObject.days.push(dayObject);
      }
    }
    return weeks;
  }

  private getMonthHeader(monthDate: DateTime): TranslatedString {
    return untranslatedString(formatMonthYearLong(monthDate, BOOT_LOCALE));
  }

  private updateDateHeaders() {
    // short date for date display row: "Sat, Oct 6"
    this.shortDateHeader = formatWeekdayDayMonthShort(this.date, BOOT_LOCALE);
  }

  updateUI() {
    this.weekdays = [];
    this.updateDateHeaders();
    this.monthYearHeader = this.getMonthHeader(this.viewDate);

    // generate weekdays with 2-letter abbreviations in display order
    const displayOrder = this.settings.getWeekDayDisplayOrder();
    const monday = DateTime.local().startOf('week'); // Luxon weeks start on Monday
    for (const isoIdx of displayOrder) {
      // isoIdx: 0=Mon, 1=Tue, ..., 6=Sun
      const day = monday.plus({ days: isoIdx });
      const dayObject = { name: day.weekdayShort.substring(0, 2) };
      this.weekdays.push(dayObject);
    }

    // generate weeks for current month only
    this.weeks = this.generateWeeksForMonth(this.viewDate);
  }

  onMonthBackClick() {
    this.viewDate = this.viewDate.minus({ months: 1 });
    this.updateUI();
    this.cdr.markForCheck();
  }

  onMonthForwardClick() {
    this.viewDate = this.viewDate.plus({ months: 1 });
    this.updateUI();
    this.cdr.markForCheck();
  }

  onDayClick(d: { id: string }) {
    this.selectDate(DateTime.fromISO(d.id));
    this.updateDateHeaders();
    this.cdr.markForCheck();
  }

  async onOKClick() {
    await this.returnAsync({ dateISO: this.date.toISODate() });
  }

  async onCancelClick() {
    await this.returnAsync({ dateISO: null });
  }

  onHeaderClick() {
    // from Calendar → YearPicker, from picker modes → Calendar
    if (this.viewMode === ViewMode.Calendar) {
      this.viewMode = ViewMode.YearPicker;
      this.cdr.markForCheck();
      void (async () => {
        await sleep(0);
        this.scrollToCurrentYear();
      })();
    } else {
      this.viewMode = ViewMode.Calendar;
      this.updateUI();
      this.cdr.markForCheck();
    }
  }

  private scrollToCurrentYear() {
    const container = this.yearGridRef?.nativeElement;
    if (!container) return;

    const currentYearCell = container.querySelector(`[data-year="${this.viewDate.year}"]`) as HTMLElement;
    if (currentYearCell) {
      const containerHeight = container.clientHeight;
      const cellTop = currentYearCell.offsetTop;
      const cellHeight = currentYearCell.offsetHeight;
      container.scrollTop = cellTop - (containerHeight / 2) + (cellHeight / 2);
    }
  }

  onYearSelect(year: number) {
    this.viewDate = DateTime.fromObject({ day: 1, month: this.viewDate.month, year });
    this.viewMode = ViewMode.MonthPicker;
    this.cdr.markForCheck();
  }

  onMonthSelect(monthIndex: number) {
    this.viewDate = DateTime.fromObject({ day: 1, month: monthIndex, year: this.viewDate.year });
    this.viewMode = ViewMode.Calendar;
    this.updateUI();
    this.cdr.markForCheck();
  }

  onTodayClick() {
    const today = this.debug.getNowLocal();
    this.selectDate(today);
    this.viewDate = today;
    this.updateUI();
    this.cdr.markForCheck();
  }

}
